import { PortfolioItemModel } from "../../../features/portfolio-info/types/PortfolioItemModel";
import { AssetType } from "../../types/AssetType";
import { blackScholesModelCalculationService } from "../BlackScholesModelCalculationService";
import { PositionModel } from "../../../features/portfolio-info/types/PositionModel";
import { PortfolioGroupedItem } from "../../../screens/positions-and-orders/models/PortfolioGroupedItem";

export class BalanceCalculationService {
    calculateAtCurrent(portfolioItems: PortfolioItemModel[], underlyingPrice: number): number {
        let balance = 0;

        portfolioItems.map(item => {
            if (item.isActive)
                balance += this.calculate(item, underlyingPrice, new Date());
        });

        return balance;
    }

    calculateAtCustom(portfolioItems: PortfolioItemModel[], underlyingPrice: number, customDate: Date): number {
        let balance = 0;

        portfolioItems.map(item => {
            if (item.isActive)
                balance += this.calculate(item, underlyingPrice, customDate);
        });

        return balance;
    }
    
    calculateAtExpiration(portfolioItems: PortfolioItemModel[], underlyingPrice: number): number {
        let balance = 0;

        portfolioItems.map(item => {
            if (item.isActive)
                balance += this.calculateBalanceAtExpiration(item, underlyingPrice);
        });

        return balance;
    }
    
    calculate(item: PortfolioItemModel, underlyingPrice: number, fromDate: Date): number {
        if (item.type === AssetType.Futures || item.type === AssetType.Spot){
            return (underlyingPrice - item.openPrice) * item.size;
        }
        else if (item.type === AssetType.Call || item.type === AssetType.Put) {
            if (item.expireAt === null)
                throw new Error("Options expiration date is null")
            
            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(item.expireAt) < 0;
            
            const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(item.expireAt, fromDate);
            
            return isExpired || timeToExpirationInShareOfYear < 0
                ? this.calculateBalanceAtExpiration(item, underlyingPrice)
                : (this.calculateOptionMarkPrice(item, underlyingPrice, timeToExpirationInShareOfYear) - item.openPrice) * item.size;
        }

        throw new Error("Unknown AssetType");
    }
    
    calculateOptionMarkPrice(item: PortfolioItemModel,
        underlyingPrice: number, timeToExpirationInShareOfYear: number
    ): number {
        if (item.markImpliedVolatility === null) {
            throw new Error("ImpliedVolatility cannot be null!");
        }

        if (item.strike === null) {
            throw new Error("Portfolio item property 'strike' is null.")
        }

        return blackScholesModelCalculationService.getPremium(
            item.type, underlyingPrice, item.strike,
            timeToExpirationInShareOfYear, item.markImpliedVolatility);
    }

    calculateGroupedPositionBalanceAfterExpiration(position: PortfolioGroupedItem): number {
        if (position.type !== AssetType.Call && position.type !== AssetType.Put)
            throw new Error("Items must be only option")

        if (position.expireAt === null)
            throw new Error("Options expiry date is null")

        if (position.expirationPrice === null)
            throw new Error("Options expiry price is null")

        if (position.strike === null)
            throw new Error("Options strike is null")

        const value = position.type === AssetType.Call
            ? position.strike < position.expirationPrice
                ? (position.expirationPrice - position.strike - position.averageOpenPrice!)
                : - position.averageOpenPrice!
            : position.strike > position.expirationPrice
                ? (position.strike - position.expirationPrice - position.averageOpenPrice!)
                : - position.averageOpenPrice!;

        return value * position.currentSize;
    }
    
    calculatePositionBalanceAfterExpiration(position: PositionModel): number {
        if (position.type !== AssetType.Call && position.type !== AssetType.Put)
            throw new Error("Items must be only option")

        if (position.expireAt === null)
            throw new Error("Options expiry date is null")

        if (position.expirationPrice === null)
            throw new Error("Options expiry price is null")

        if (position.strike === null)
            throw new Error("Options strike is null")

        return position.type === AssetType.Call
            ? position.strike < position.expirationPrice
                ? position.volume + (position.expirationPrice - position.strike) * position.size
                : position.volume
            : position.strike > position.expirationPrice
                ? position.volume + (position.strike - position.expirationPrice) * position.size
                : position.volume;
    }
    
    calculateOrderBalanceAfterExpiration(order: PortfolioItemModel): number {
        if (order.type !== AssetType.Call && order.type !== AssetType.Put)
            throw new Error("Items must be only option")

        if (order.expireAt === null)
            throw new Error("Options expiry date is null")

        if (order.expirationPrice === null)
            throw new Error("Options expiry price is null")

        if (order.strike === null)
            throw new Error("Options strike is null")

        const value = order.type === AssetType.Call
            ? order.strike < order.expirationPrice
                ? (order.expirationPrice - order.strike - order.openPrice)
                : - order.openPrice
            : order.strike > order.expirationPrice
                ? (order.strike - order.expirationPrice - order.openPrice)
                : - order.openPrice;
        
        return value * order.size;
    }
    
    private calculateBalanceAtExpiration(item: PortfolioItemModel, underlyingPrice: number): number {
        if (item.type === AssetType.Call) {
            if (item.strike === null) {
                throw new Error("Portfolio item property 'strike' is null.")
            }

            if (item.expireAt === null)
                throw new Error("Options expiration date is null.")

            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(item.expireAt) < 0;

            return !isExpired
                ? item.strike < underlyingPrice
                    ? (underlyingPrice - item.strike) * item.size - item.openPrice * item.size
                    : - item.openPrice * item.size
                : this.calculateOrderBalanceAfterExpiration(item);
        }
        else if (item.type === AssetType.Put) {
            if (item.strike === null) {
                throw new Error("Portfolio item property 'strike' is null.")
            }

            if (item.expireAt === null)
                throw new Error("Options expiration date is null.")

            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(item.expireAt) < 0;

            return !isExpired
                ? item.strike > underlyingPrice
                    ? (item.strike - underlyingPrice) * item.size - item.openPrice * item.size
                    : - item.openPrice * item.size
                : this.calculateOrderBalanceAfterExpiration(item);
        }
        else if (item.type === AssetType.Futures || item.type === AssetType.Spot) {
            return (underlyingPrice - item.openPrice) * item.size;
        }

        throw new Error("Unknown AssetType");
    }
}